// ============================================================
// Pay Rocky - Buy Crypto Page
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, CheckCircle, Upload, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

import { PublicLayout, PageContainer, SectionHeading } from '../components/layout/Layout';
import { Button, Card, FormField, TextInput, SelectInput, Divider } from '../components/ui/Primitives';
import { useAuth } from '../context/AuthContext';
import { useRates } from '../context/RatesContext';
import { useOrders } from '../context/OrdersContext';
import { useBankSettings } from '../context/BankSettingsContext';
import { useWallets } from '../context/WalletsContext';
import { ORDER_LIMITS, SUPPORT_EMAIL, TRADABLE_CRYPTO_LIST } from '../data/constants';
import { useSupportEmail } from '../hooks/useSupportEmail';
import { formatNGN, formatCrypto, validateWalletAddress, formatCountdown, secondsUntil } from '../utils/helpers';

const STEPS = ['Configure', 'Review & Place', 'Pay & Upload'];

export default function BuyPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { rates, getBuyFeeUsd } = useRates();
  const { placeBuyOrder, uploadProof, cancelOrder, requestQuoteLock } = useOrders();
  const { bankDetails, hasCompleteBankDetails } = useBankSettings();
  const { getWalletConfig } = useWallets();
  const supportEmail = useSupportEmail(SUPPORT_EMAIL);
  const isSuspended = currentUser?.kyc_status === 'suspended';
  const isBuyRestricted = currentUser && ['unverified', 'rejected', 'suspended'].includes(currentUser.kyc_status);

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [quoteLock, setQuoteLock] = useState(null);
  const [selectedProofName, setSelectedProofName] = useState('');
  const [selectedProofMeta, setSelectedProofMeta] = useState(null);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    cryptoId: 'usdt',
    network: 'TRC20',
    usdAmount: '100',
    walletAddress: '',
    walletAddressExpires: 'no',
    walletExpiryContactPhone: '',
    walletExpiryMinutes: '',
    paymentNameConfirmed: false,
  });
  const [errors, setErrors] = useState({});

  const crypto = TRADABLE_CRYPTO_LIST.find((item) => item.id === form.cryptoId);
  const walletConfig = getWalletConfig(form.cryptoId, form.network);
  const isBtcOrder = form.cryptoId === 'btc';
  const hasExpiringWallet = isBtcOrder && form.walletAddressExpires === 'yes';
  const buyLimits = ORDER_LIMITS.buy.byCrypto[form.cryptoId] || ORDER_LIMITS.buy.default;
  const rate = rates[form.cryptoId];
  const usdAmt = parseFloat(form.usdAmount) || 0;
  const buyFeeUsd = Number(quoteLock?.fee_usd ?? getBuyFeeUsd(form.cryptoId, form.network));
  const totalUsdAmt = usdAmt + buyFeeUsd;
  const lockedUsdPrice = Number(quoteLock?.usd_price || rate?.usdPrice || 0);
  const lockedBuyRate = Number(quoteLock?.rate_used || rate?.buy || 0);
  const cryptoAmt = lockedUsdPrice ? usdAmt / lockedUsdPrice : 0;
  const nairaAmt = lockedUsdPrice ? (totalUsdAmt * lockedBuyRate) / lockedUsdPrice : 0;
  const awaitingProof = String(placedOrder?.status || '').toUpperCase() === 'PENDING';

  useEffect(() => {
    const selected = TRADABLE_CRYPTO_LIST.find((item) => item.id === form.cryptoId);
    if (selected) {
      setForm((prev) => ({
        ...prev,
        network: selected.networks[0],
        walletAddressExpires: selected.id === 'btc' ? prev.walletAddressExpires : 'no',
        walletExpiryContactPhone: selected.id === 'btc' ? prev.walletExpiryContactPhone : '',
        walletExpiryMinutes: selected.id === 'btc' ? prev.walletExpiryMinutes : '',
      }));
    }
  }, [form.cryptoId]);

  useEffect(() => {
    const targetExpiry = placedOrder?.rate_locked_until || quoteLock?.expires_at;
    if (!targetExpiry) return undefined;
    setCountdown(secondsUntil(targetExpiry));
    const intervalId = setInterval(() => {
      const seconds = secondsUntil(targetExpiry);
      setCountdown(seconds);
      if (seconds === 0) clearInterval(intervalId);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [placedOrder, quoteLock]);

  useEffect(() => {
    let cancelled = false;
    if (!currentUser || isBuyRestricted || !form.cryptoId || !form.network) {
      setQuoteLock(null);
      return undefined;
    }
    if (walletConfig?.isEnabled === false) {
      setQuoteLock(null);
      return undefined;
    }

    requestQuoteLock({
      type: 'buy',
      cryptoType: form.cryptoId,
      network: form.network,
    }).then((nextQuoteLock) => {
      if (!cancelled) setQuoteLock(nextQuoteLock);
    }).catch(() => {
      if (!cancelled) setQuoteLock(null);
    });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, form.cryptoId, form.network, isBuyRestricted, requestQuoteLock, walletConfig?.isEnabled]);

  const handleCancelPlacedOrder = async () => {
    if (!placedOrder?.order_id) return;
    setLoading(true);
    const response = await cancelOrder(placedOrder.order_id);
    setLoading(false);

    if (response.success) {
      setPlacedOrder(response.order);
      toast.success(response.message || 'Order cancelled.');
      navigate('/dashboard');
    } else {
      toast.error(response.error || 'We could not cancel this order right now.');
    }
  };

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  const setCheckbox = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.checked }));

  const validate = () => {
    const nextErrors = {};
    if (!usdAmt || usdAmt < buyLimits.minUsd) {
      nextErrors.usdAmount = `Minimum buy amount for ${crypto?.symbol || 'this asset'} is $${buyLimits.minUsd}`;
    } else if (buyLimits.maxUsd && usdAmt > buyLimits.maxUsd) {
      nextErrors.usdAmount = `Maximum buy amount for ${crypto?.symbol || 'this asset'} is $${buyLimits.maxUsd}`;
    }
    if (!validateWalletAddress(form.walletAddress, form.network)) {
      nextErrors.walletAddress = `Invalid ${form.network} wallet address`;
    }
    if (hasExpiringWallet) {
      if (!form.walletExpiryContactPhone.trim()) {
        nextErrors.walletExpiryContactPhone = 'Enter the phone number we should contact if the BTC wallet expires';
      }
      if (!parseInt(form.walletExpiryMinutes, 10) || parseInt(form.walletExpiryMinutes, 10) < 1) {
        nextErrors.walletExpiryMinutes = 'Enter how many minutes remain before the BTC wallet expires';
      }
    }
    if (!form.paymentNameConfirmed) {
      nextErrors.paymentNameConfirmed = 'You must confirm that the payment name matches your verified profile name';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleProceed = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (isBuyRestricted) {
      toast.error(isSuspended ? 'Your profile is suspended from buy orders.' : 'You must be verified before buying crypto.');
      navigate('/dashboard');
      return;
    }
    if (!hasCompleteBankDetails) {
      toast.error('Bank payment details are not available yet. Please contact PayRocky.');
      return;
    }
    if (walletConfig?.isEnabled === false) {
      toast.error(walletConfig?.disabledReason || `${form.network} is temporarily unavailable for buy orders.`);
      return;
    }
    if (validate()) setStep(1);
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    const response = await placeBuyOrder({
      userId: currentUser.id,
      userName: currentUser.name,
      cryptoType: form.cryptoId,
      cryptoAmount: cryptoAmt,
      usdValue: usdAmt,
      feeUsd: buyFeeUsd,
      totalUsdValue: totalUsdAmt,
      nairaValue: nairaAmt,
      rateUsed: rate.buy,
      walletAddress: form.walletAddress,
      network: form.network,
      payerName: currentUser.name,
      walletAddressExpires: hasExpiringWallet,
      walletExpiryContactPhone: hasExpiringWallet ? form.walletExpiryContactPhone.trim() : '',
      walletExpiryMinutes: hasExpiringWallet ? parseInt(form.walletExpiryMinutes, 10) : 0,
      quoteLockToken: quoteLock?.token || '',
      paymentNameConfirmed: form.paymentNameConfirmed,
    });
    setLoading(false);

    if (response.success) {
      setPlacedOrder(response.order);
      setStep(2);
      toast.success('Order placed! Make payment within 15 minutes.');
    } else {
      toast.error(response.error || 'Something went wrong. Try again.');
    }
  };

  const handleUploadProof = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    const response = await uploadProof(placedOrder.order_id, file);
    setLoading(false);

    if (response.success) {
      setPlacedOrder(response.order);
      setSelectedProofName(file.name);
      toast.success("Proof uploaded! We'll verify and send your crypto shortly.");
    } else {
      toast.error('Upload failed. Try again.');
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const handleProofSelection = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedProofName('');
      setSelectedProofMeta(null);
      return;
    }

    setSelectedProofName(file.name);
    setSelectedProofMeta({
      name: file.name,
      type: file.type || 'Unknown file type',
      sizeLabel: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
    });
  };

  return (
    <PublicLayout>
      <PageContainer style={{ maxWidth: '760px' }}>
        <SectionHeading tag="Buy Crypto" title="Buy with Naira bank transfer" subtitle="Enter your details, pay by bank transfer, and receive crypto in your wallet." />

        {isBuyRestricted ? (
          <Card className="fade-up" style={{ background: 'linear-gradient(135deg, rgba(176, 58, 48, 0.08) 0%, rgba(255,255,255,0.96) 100%)' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <AlertTriangle size={18} style={{ color: 'var(--clr-danger)', flexShrink: 0, marginTop: '2px' }} />
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--clr-text)', marginBottom: '6px' }}>
                  Buying is unavailable for your account
                </div>
                <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7, marginBottom: '12px' }}>
                  {isSuspended ? (
                    <>
                      Your profile is currently suspended from buy orders. You can still place sell orders. To restore full access, contact PayRocky support at{' '}
                      <a href={`mailto:${supportEmail}`} style={{ color: 'var(--clr-accent)', fontWeight: 700 }}>
                        {supportEmail}
                      </a>.
                    </>
                  ) : (
                    'Users with unverified or rejected verification status can only sell crypto. Complete your profile verification in the dashboard before placing buy orders.'
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Button variant="primary" size="sm" onClick={() => navigate('/sell')}>Sell Crypto</Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <>
            <StepBar steps={STEPS} current={step} />

            {step === 0 && (
              <Card className="fade-up">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <FormField label="Cryptocurrency">
                    <SelectInput value={form.cryptoId} onChange={set('cryptoId')}>
                      {TRADABLE_CRYPTO_LIST.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.symbol})</option>)}
                    </SelectInput>
                  </FormField>

                  <FormField label="Network">
                    <SelectInput value={form.network} onChange={set('network')}>
                      {crypto?.networks.map((network) => <option key={network} value={network}>{network}</option>)}
                    </SelectInput>
                  </FormField>
                </div>

                <FormField
                  label="Amount in USD"
                  error={errors.usdAmount}
                  hint={`Minimum $${buyLimits.minUsd}${buyLimits.maxUsd ? ` • Maximum $${buyLimits.maxUsd}` : ''}`}
                >
                  <TextInput
                    type="number"
                    min={String(buyLimits.minUsd)}
                    max={buyLimits.maxUsd ? String(buyLimits.maxUsd) : undefined}
                    placeholder="e.g. 100"
                    value={form.usdAmount}
                    onChange={set('usdAmount')}
                    error={errors.usdAmount}
                  />
                </FormField>

                {rate && (
                  <div style={{ background: 'rgba(143, 59, 46, 0.08)', border: '1px solid rgba(143, 59, 46, 0.16)', borderRadius: 'var(--radius-md)', padding: '12px 14px', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--clr-text)', marginBottom: '4px' }}>
                      Buy fee applies to {crypto?.name} on {form.network}
                    </div>
                    <div style={{ fontSize: '12px', lineHeight: 1.7, color: 'var(--clr-text-2)' }}>
                      This network has an additional ${buyFeeUsd.toFixed(2)} processing fee. If you enter ${usdAmt.toFixed(2)}, your total payable amount becomes ${totalUsdAmt.toFixed(2)} before the Naira conversion is shown below.
                    </div>
                  </div>
                )}

                {quoteLock ? (
                  <div style={{ background: 'rgba(122, 75, 53, 0.08)', border: '1px solid rgba(122, 75, 53, 0.18)', borderRadius: 'var(--radius-md)', padding: '11px 14px', marginBottom: '1rem', fontSize: '12px', lineHeight: 1.7, color: 'var(--clr-text-2)' }}>
                    Your quote is locked for this {crypto?.symbol} {form.network} buy route. Time remaining: <strong style={{ color: 'var(--clr-text)' }}>{formatCountdown(countdown)}</strong>.
                  </div>
                ) : null}

                {walletConfig?.isEnabled === false ? (
                  <div style={{ background: 'rgba(176, 58, 48, 0.08)', border: '1px solid rgba(176, 58, 48, 0.18)', borderRadius: 'var(--radius-md)', padding: '11px 14px', marginBottom: '1rem', fontSize: '12px', lineHeight: 1.7, color: 'var(--clr-text-2)' }}>
                    {walletConfig?.disabledReason || `${form.network} is temporarily unavailable for this asset. Please choose another network or contact support.`}
                  </div>
                ) : null}

                <div style={{ background: 'rgba(255, 184, 48, 0.14)', border: '1px solid rgba(255,184,48,0.28)', borderRadius: 'var(--radius-md)', padding: '12px 14px', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--clr-text)', marginBottom: '4px' }}>
                    KYC payment-name rule
                  </div>
                  <div style={{ fontSize: '12px', lineHeight: 1.7, color: 'var(--clr-text-2)' }}>
                    Because your profile is verified, your bank transfer must come from an account name that matches your verified profile name exactly: <strong style={{ color: 'var(--clr-text)' }}>{currentUser?.name}</strong>. Third-party payments can delay or cancel the order.
                  </div>
                </div>

                <FormField label={`Your ${crypto?.symbol} wallet address (${form.network})`} error={errors.walletAddress}>
                  <TextInput placeholder={`Paste your ${form.network} wallet address`} value={form.walletAddress} onChange={set('walletAddress')} error={errors.walletAddress} />
                </FormField>

                {isBtcOrder && (
                  <>
                    <FormField label="Does the wallet address expire?">
                      <SelectInput value={form.walletAddressExpires} onChange={set('walletAddressExpires')}>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </SelectInput>
                    </FormField>

                    {hasExpiringWallet && (
                      <div
                        className="buy-btc-expiry-grid"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '14px',
                          marginBottom: '1rem',
                        }}
                      >
                        <FormField label="Phone number to contact" error={errors.walletExpiryContactPhone}>
                          <TextInput
                            placeholder="e.g. 08012345678"
                            value={form.walletExpiryContactPhone}
                            onChange={(event) => setForm((prev) => ({ ...prev, walletExpiryContactPhone: event.target.value }))}
                            error={errors.walletExpiryContactPhone}
                          />
                        </FormField>

                        <FormField label="How many minutes?" error={errors.walletExpiryMinutes}>
                          <TextInput
                            type="number"
                            min="1"
                            placeholder="e.g. 15"
                            value={form.walletExpiryMinutes}
                            onChange={(event) => setForm((prev) => ({ ...prev, walletExpiryMinutes: event.target.value.replace(/[^\d]/g, '') }))}
                            error={errors.walletExpiryMinutes}
                          />
                        </FormField>
                      </div>
                    )}

                    <div style={{ background: 'rgba(143, 59, 46, 0.08)', border: '1px solid rgba(143, 59, 46, 0.16)', borderRadius: 'var(--radius-md)', padding: '11px 14px', marginBottom: '1rem', fontSize: '12px', lineHeight: 1.7, color: 'var(--clr-text-2)' }}>
                      This BTC-only check helps us know if your receiving wallet could expire quickly. If you choose <strong style={{ color: 'var(--clr-text)' }}>Yes</strong>, we will keep the contact number and expected time window with your order.
                    </div>
                  </>
                )}

                <FormField error={errors.paymentNameConfirmed}>
                  <label className="buy-payment-confirmation" style={{ display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr)', alignItems: 'start', gap: '10px', cursor: 'pointer' }}>
                    <input className="buy-payment-confirmation__checkbox" type="checkbox" checked={form.paymentNameConfirmed} onChange={setCheckbox('paymentNameConfirmed')} />
                    <span className="buy-payment-confirmation__text" style={{ display: 'block', fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7 }}>
                      I confirm that I will make this payment from a bank account under <strong style={{ color: 'var(--clr-text)' }}>{currentUser?.name}</strong>, which matches my verified profile name.
                    </span>
                  </label>
                </FormField>

                {usdAmt > 0 && rate && (
                  <CalcBox
                    rows={[
                      { label: 'You receive', value: `${formatCrypto(cryptoAmt, crypto?.symbol)} ${crypto?.symbol}` },
                      { label: 'Entered amount', value: `$${usdAmt.toFixed(2)}` },
                      { label: `${form.network} fee`, value: `$${buyFeeUsd.toFixed(2)}` },
                      { label: 'Total billable amount', value: `$${totalUsdAmt.toFixed(2)}` },
                      { label: 'Rate (buy)', value: `${formatNGN(rate.buy)} / ${crypto?.symbol}` },
                    ]}
                    totalLabel="You pay"
                    totalValue={formatNGN(nairaAmt)}
                    accent
                  />
                )}

                <Button variant="primary" size="lg" fullWidth onClick={handleProceed} disabled={walletConfig?.isEnabled === false}>
                  Continue to Review
                </Button>
              </Card>
            )}

            {step === 1 && (
              <Card className="fade-up">
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: 700, marginBottom: '16px' }}>
                  Review your order
                </div>

                <OrderRow label="Buying" value={`${formatCrypto(cryptoAmt, crypto?.symbol)} ${crypto?.symbol}`} />
                <OrderRow label="Network" value={form.network} />
                <OrderRow label="Verified profile name" value={currentUser?.name} />
                {isBtcOrder && <OrderRow label="Wallet expires" value={hasExpiringWallet ? 'Yes' : 'No'} />}
                {hasExpiringWallet && <OrderRow label="Contact phone" value={form.walletExpiryContactPhone} />}
                {hasExpiringWallet && <OrderRow label="Expiry window" value={`${parseInt(form.walletExpiryMinutes, 10)} minutes`} />}
                <OrderRow label="Crypto value" value={`$${usdAmt.toFixed(2)}`} />
                <OrderRow label={`${form.network} fee`} value={`$${buyFeeUsd.toFixed(2)}`} />
                <OrderRow label="Total payable" value={`$${totalUsdAmt.toFixed(2)}`} />
                <OrderRow label="Rate" value={`${formatNGN(lockedBuyRate)} / ${crypto?.symbol}`} />
                <OrderRow label="Wallet" value={form.walletAddress} mono truncate />
                <Divider />
                <OrderRow label="You pay (NGN)" value={formatNGN(nairaAmt)} accent big />

                <div style={{ background: 'rgba(143, 59, 46, 0.08)', border: '1px solid rgba(143, 59, 46, 0.16)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: '12px', color: 'var(--clr-text-2)', marginTop: '1rem', lineHeight: 1.7 }}>
                  The ${buyFeeUsd.toFixed(2)} {form.network} fee is paid by the buyer and has already been added to your total payable amount.
                </div>

                <div style={{ background: 'rgba(255, 184, 48, 0.14)', border: '1px solid rgba(255,184,48,0.28)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: '12px', color: 'var(--clr-text-2)', marginTop: '1rem', lineHeight: 1.7 }}>
                  Payment must be sent from a bank account bearing <strong style={{ color: 'var(--clr-text)' }}>{currentUser?.name}</strong>, which matches your verified profile record.
                </div>

                <div style={{ background: 'var(--clr-warn-dim)', border: '1px solid rgba(255,184,48,0.25)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: '12px', color: 'var(--clr-warn)', marginTop: '1rem', marginBottom: '1rem', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                      This quote is locked for {quoteLock?.rate_lock_minutes || 15} minutes. Make payment promptly after placing the order.
                </div>

                {!hasCompleteBankDetails && (
                  <div style={{ background: 'rgba(176, 58, 48, 0.08)', border: '1px solid rgba(176, 58, 48, 0.16)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: '12px', color: 'var(--clr-text-2)', marginBottom: '1rem', lineHeight: 1.7 }}>
                    PayRocky has not configured the bank payment details yet. Buying cannot continue until the receiving account has been set for this payment flow.
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button variant="ghost" onClick={() => setStep(0)}>Back</Button>
                  <Button variant="primary" fullWidth loading={loading} onClick={handlePlaceOrder} disabled={!hasCompleteBankDetails}>
                    Confirm & Place Order
                  </Button>
                </div>
              </Card>
            )}

            {step === 2 && placedOrder && (
              <Card className="fade-up">
                {awaitingProof ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: 700 }}>Make payment</div>
                        <div style={{ fontSize: '12px', color: 'var(--clr-text-2)', marginTop: '2px' }}>
                          Order <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--clr-accent)' }}>{placedOrder.order_id}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '2px' }}>Rate expires in</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '18px', fontWeight: 600, color: countdown < 180 ? 'var(--clr-danger)' : 'var(--clr-warn)' }}>
                          {formatCountdown(countdown)}
                        </div>
                      </div>
                    </div>

                    <div style={{ background: 'var(--clr-surface-2)', borderRadius: 'var(--radius-md)', padding: '1rem', marginBottom: '1rem' }}>
                      <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '12px' }}>
                        Bank transfer details
                      </div>
                      {[
                        { label: 'Bank', val: bankDetails.bank_name || 'Not configured' },
                        { label: 'Account name', val: bankDetails.account_name || 'Not configured' },
                        { label: 'Account number', val: bankDetails.account_number || 'Not configured', copy: Boolean(bankDetails.account_number) },
                        { label: 'Your payment name', val: placedOrder.payer_name || currentUser?.name },
                        { label: 'Crypto value', val: `$${placedOrder.usd_value?.toFixed(2)}` },
                        { label: `${placedOrder.network} fee`, val: `$${(placedOrder.fee_usd || 0).toFixed(2)}` },
                        { label: 'Total payable', val: `$${(placedOrder.total_usd_value || placedOrder.usd_value || 0).toFixed(2)}` },
                        { label: 'Amount (NGN)', val: formatNGN(placedOrder.naira_value), copy: true, accent: true },
                      ].map(({ label, val, copy, accent }) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>{label}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: accent ? 'var(--clr-accent)' : 'var(--clr-text)' }}>
                            {val}
                            {copy && (
                              <button onClick={() => copyText(val)} style={{ color: 'var(--clr-text-3)', padding: '2px' }}>
                                <Copy size={12} />
                              </button>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>

                    <FormField label="Upload proof of payment" hint="Accepted: JPG, PNG, PDF (max 5MB)">
                      <div onClick={() => fileRef.current?.click()} style={{ border: '2px dashed var(--clr-border-md)', borderRadius: 'var(--radius-md)', padding: '1.5rem', textAlign: 'center', cursor: 'pointer', color: 'var(--clr-text-2)', fontSize: '13px', transition: 'border-color 0.15s' }}>
                        <Upload size={22} style={{ marginBottom: '6px', opacity: 0.5 }} />
                        <div style={{ fontWeight: 700, color: 'var(--clr-text)' }}>
                          {selectedProofName || 'Click to select file'}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginTop: '4px' }}>
                          {selectedProofName ? 'File selected and ready to upload' : 'or drag & drop'}
                        </div>
                      </div>
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        style={{ display: 'none' }}
                        onChange={handleProofSelection}
                      />

                      {selectedProofMeta && (
                        <div style={{ marginTop: '12px', padding: '12px 14px', borderRadius: 'var(--radius-md)', background: 'rgba(143, 59, 46, 0.08)', border: '1px solid rgba(143, 59, 46, 0.16)', textAlign: 'left' }}>
                          <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                            Selected file
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--clr-text)', marginBottom: '4px', wordBreak: 'break-word' }}>
                            {selectedProofMeta.name}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--clr-text-2)' }}>
                            {selectedProofMeta.type} - {selectedProofMeta.sizeLabel}
                          </div>
                        </div>
                      )}
                    </FormField>

                    <Button variant="primary" size="lg" fullWidth loading={loading} onClick={handleUploadProof}>
                      Submit Payment Proof
                    </Button>
                    <div style={{ height: '10px' }} />
                    <Button variant="ghost" size="lg" fullWidth onClick={handleCancelPlacedOrder} loading={loading}>
                      Cancel Order
                    </Button>
                  </>
                ) : (
                  <SuccessState orderId={placedOrder.order_id} onDashboard={() => navigate('/dashboard')} />
                )}
              </Card>
            )}
          </>
        )}
      </PageContainer>
      <style>{`
        @media (max-width: 720px) {
          .buy-btc-expiry-grid {
            grid-template-columns: 1fr !important;
          }
        }

        .buy-payment-confirmation {
          width: 100%;
        }

        .buy-payment-confirmation__text {
          min-width: 0;
          overflow-wrap: anywhere;
        }

        .buy-payment-confirmation__checkbox {
          width: 16px;
          height: 16px;
          margin: 2px 0 0 0;
          accent-color: var(--clr-accent);
          justify-self: start;
          flex-shrink: 0;
        }

        @media (max-width: 560px) {
          .buy-payment-confirmation__text {
            font-size: 12px !important;
            line-height: 1.75 !important;
          }
        }
      `}</style>
    </PublicLayout>
  );
}

function StepBar({ steps, current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem', gap: '4px' }}>
      {steps.map((label, index) => (
        <React.Fragment key={label}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: index < current ? 'var(--clr-accent)' : index === current ? 'var(--clr-accent-dim)' : 'var(--clr-surface-2)', border: `1px solid ${index <= current ? 'var(--clr-accent)' : 'var(--clr-border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: index < current ? 'var(--clr-on-accent)' : index === current ? 'var(--clr-accent)' : 'var(--clr-text-3)' }}>
              {index < current ? <CheckCircle size={12} /> : index + 1}
            </div>
            <span style={{ fontSize: '12px', fontWeight: index === current ? 600 : 400, color: index === current ? 'var(--clr-text)' : 'var(--clr-text-3)' }}>{label}</span>
          </div>
          {index < steps.length - 1 && <div style={{ flex: 1, height: '1px', background: index < current ? 'var(--clr-accent)' : 'var(--clr-border)' }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function CalcBox({ rows, totalLabel, totalValue, accent }) {
  return (
    <div style={{ background: 'var(--clr-surface-2)', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: '1rem', border: '1px solid var(--clr-border)' }}>
      {rows.map((row) => (
        <Row key={row.label} l={row.label} v={row.value} />
      ))}
      <div style={{ height: '1px', background: 'var(--clr-border)', margin: '8px 0' }} />
      <Row l={totalLabel} v={totalValue} accent={accent} big />
    </div>
  );
}

function Row({ l, v, accent, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
      <span style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>{l}</span>
      <span style={{ fontSize: big ? '16px' : '13px', fontWeight: big ? 700 : 500, color: accent ? 'var(--clr-accent)' : 'var(--clr-text)' }}>{v}</span>
    </div>
  );
}

function OrderRow({ label, value, mono, truncate, accent, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--clr-border)' }}>
      <span style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>{label}</span>
      <span style={{ fontSize: big ? '16px' : '13px', fontWeight: big ? 700 : 500, fontFamily: mono ? 'var(--font-mono)' : undefined, color: accent ? 'var(--clr-accent)' : 'var(--clr-text)', maxWidth: '200px', overflow: 'hidden', textOverflow: truncate ? 'ellipsis' : undefined, whiteSpace: truncate ? 'nowrap' : undefined }}>
        {value}
      </span>
    </div>
  );
}

function SuccessState({ orderId, onDashboard }) {
  return (
    <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
      <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'var(--clr-accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
        <CheckCircle size={28} color="var(--clr-accent)" />
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 800, marginBottom: '6px' }}>Proof submitted!</div>
      <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', marginBottom: '1rem' }}>Our team will verify your payment and send crypto within 15-30 mins.</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--clr-text-3)', marginBottom: '1.5rem' }}>Order ID: <span style={{ color: 'var(--clr-accent)' }}>{orderId}</span></div>
      <Button variant="primary" onClick={onDashboard}>View in Dashboard</Button>
    </div>
  );
}

